import type { AxiosInstance, AxiosRequestConfig, AxiosStatic } from 'axios';
import { refreshTokenRequestBody, refreshTokenRequestContentType } from './oauth2Request';
import { wellKnownOidcConfigUrl } from './oidcRequest';

/*
  OpenID Connect authentication towards Mimir system in brief:

  One-time set-up:
    1. Call browserAuthorizeUrl() to interactively obtain a refresh_token, which
       you store for future use.

  Normal usage:
    1. Retrieve stored refresh_token.
    2. Call fetchTokensFromRefreshToken() to get a Mimir API access token.
    3. Perform HTTP calls towards Mimir API specifying the Mimir Access token
       using the HTTP request header 'x-mimir-cognito-id-token'.

  The browserAuthorizeUrl() and fetchTokensFromRefreshToken() functions requires
  some parameters you'll find in the Mimir service document and the OpenID
  Connect configuration document.

  The value of these parameters rarely change, but if you want to obtain them
  programmatically, here is how you do it:

   1. Perform an HTTP GET of the service document of the Mimir system. The
      service document for the Europe region is https://mimir.mjoll.no/api/v1
   2. Construct the client registration like this: {client_id:
      serviceDocument.clientConfig.appClientId, redirect_uri:
      'http://localhost:8080'}
   3. Construct the location of the OpenID Connect domain like this:
      ('https://cognito-idp.' + serviceDocument.clientConfig.authRegion +
      '.amazonaws.com/' + serviceDocument.clientConfig.userPoolId)
   3. Call fetchWellKnownOidcConfig() with the OpenID Connect domain to obtain
      the OpenID Connect configuration.


  We use OpenID Connect for authentication towards the Mimir system in the
  end-to-end test for two reasons:

   1. Most importantly, we want avoid storing and using user names and passwords
      as credentials, and instead use some form of API keys, since this conforms
      with good security practices for reducing the number of attack vectors.
      The refresh_token keys provided by OpenID Connect acts as such API keys.
      * https://medium.com/swlh/api-keys-whats-the-point-8f58d7966f9
      * https://stormpath.com/blog/top-six-reasons-use-api-keys-and-how
      * https://softwareengineering.stackexchange.com/a/341690

   2. When we've used user name and passwords for programmatic authentication
      towards the Mimir system, we've used the Amplify libraries to perform this
      operation. We've gotten complaints both from customers and our own
      developers that the Amplify libraries are cumbersome to use.
*/

/**
 * The OpenID Connect configuration needed for performing authentication.
 */
export interface OidcConfig {
  authorization_endpoint: string;
  token_endpoint: string
}

/**
 * Information identifying the client that wishes to authenticate.
 */
export interface OidcClientRegistration {
  client_id: string;
  client_secret?: string;
  redirect_uri: string;
}

/**
 * Describes how to obtain the OpenId Connect configuration using an HTTP
 * request.
 */
function wellKnownOidcConfigRequest(domain: string): AxiosRequestConfig {
  return {
    url: wellKnownOidcConfigUrl(domain)
  };
}

/**
 * Obtain the OpenID Connect configuration used for authentication.
 */
export async function fetchWellKnownOidcConfig(axios: AxiosStatic | AxiosInstance, domain: string): Promise<OidcConfig> {
  const response = await axios.request<OidcConfig>(wellKnownOidcConfigRequest(domain));
  return response.data;
}

/**
 * Get a URL that a user may open in browser in order to go through an
 * authentication process in order to obtain a temporary authorization code,
 * which can later be exchanged for a long lived refresh_token which can be used
 * to obtain Mimir API access tokens.
 */
export function browserAuthorizeUrl(authorization_endpoint: string, clientReg: OidcClientRegistration, scope: string = 'openid', additonalQueryParameters: Record<string, string> = {}) {
  const url = new URL(authorization_endpoint);
  const query = new URLSearchParams({
    /*
      Some providers will fail if a state is not provided, even though it is
      technically optional.
    */
    state: 'none',
    scope,
    response_type: 'code',
    client_id: clientReg.client_id,
    redirect_uri: clientReg.redirect_uri,
    ...additonalQueryParameters
  });
  url.search = query.toString();
  return url.toString();
}

/**
 * Describes how to exchange a temporary authorization code for a long lived
 * refresh_token using an HTTP request.
 */
function tokensFromCodeGrantRequest(code: string, clientReg: OidcClientRegistration, token_endpoint: string): AxiosRequestConfig {
  const params: Record<string, string> = {
    grant_type: 'authorization_code',
    code,
    client_id: clientReg.client_id,
    redirect_uri: clientReg.redirect_uri
  };

  if (clientReg.client_secret) {
    params.client_secret = clientReg.client_secret;
  }

  const codeGrant = new URLSearchParams(params);

  return {
    url: token_endpoint,
    data: codeGrant.toString(),
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded'
    }
  };
}

type CodeGrantedTokens = { refresh_token: string };

/**
 * Exchange a temporary authorization code for a long lived refresh_token.
 */
export async function fetchTokensFromCodeGrant(axios: AxiosStatic | AxiosInstance, code: string, clientReg: OidcClientRegistration, token_endpoint: string): Promise<CodeGrantedTokens> {
  const response = await axios.request<CodeGrantedTokens>(tokensFromCodeGrantRequest(code, clientReg, token_endpoint));
  return response.data;
}

/**
 * Describes how to exchange a long lived refresh_token for a Mimir API access
 * token using an HTTP request.
 */
export function tokensFromRefreshTokenRequest(refresh_token: string, clientReg: OidcClientRegistration, token_endpoint: string): AxiosRequestConfig {
  return {
    url: token_endpoint,
    data: refreshTokenRequestBody(refresh_token, clientReg.client_id),
    method: 'POST',
    headers: {
      'Content-Type': refreshTokenRequestContentType
    }
  };
}

export type RefreshedTokens = { id_token: string, access_token: string };

/**
 * Exchange a long lived refresh token for a Mimir API access token. The returned
 * id_token is a Mimir API access token can be passed in the HTTP request header
 * 'x-mimir-cognito-id-token' when talking to Mimir APIs in order to
 * authenticate the request.
 */
export async function fetchTokensFromRefreshToken(axios: AxiosInstance, refresh_token: string, clientReg: OidcClientRegistration, token_endpoint: string): Promise<RefreshedTokens> {
  const response = await axios.request<RefreshedTokens>(tokensFromRefreshTokenRequest(refresh_token, clientReg, token_endpoint));
  return response.data;
}
